import { useCallback } from "react";

import { useApiContext } from "../../../../../api/context";
import { useCustomerId } from "../../../../../Components/hooks/useCustomerId";
import { type AWSAccount } from "../../../../Settings/AWS/types";

type AWSPermissions = {
  [permissionsNamespace: string]: string[];
};

type GrantedAWSPermissions = {
  awsPermissions: AWSPermissions;
  hasPolicy: boolean;
};

export type AwsPermissionsCheckResult = {
  permissionsExists: boolean;
  missingPermissions: string[];
  commandToGrantPermission: string;
};

function generateCreatePolicyCommand(roleName: string, policyName: string, awsPermissions: string[]) {
  return `aws iam put-role-policy \\
--role-name ${roleName} \\
--policy-name ${policyName} \\
--policy-document '${JSON.stringify({
    Version: "2012-10-17",
    Statement: [
      {
        Action: awsPermissions,
        Resource: "*",
        Effect: "Allow",
      },
    ],
  })}'`;
}

function generateUpdatePolicyCommand(roleName: string, policyName: string, awsPermissions: string[]) {
  return `aws iam put-role-policy \\
--role-name ${roleName} \\
--policy-name ${policyName} \\
--policy-document "$(aws iam get-role-policy --role-name ${roleName} --policy-name ${policyName} --output json | jq '. | {Statement: .PolicyDocument.Statement, Version: .PolicyDocument.Version} | .Statement[0].Action += ${JSON.stringify(
    awsPermissions
  )}'  | jq -c)"`;
}

export function areAwsPermissionsGranted(
  awsPermissions: AWSPermissions,
  permissionsNamespace: string,
  operationIds: string[]
): Record<string, boolean> {
  const result: Record<string, boolean> = {};

  if (awsPermissions["*"]?.includes("*")) {
    operationIds.forEach((op) => {
      result[op] = true;
    });

    return result;
  }

  const permissions = awsPermissions[permissionsNamespace] || [];
  for (const operationId of operationIds) {
    result[operationId] = permissions.some((perm) => {
      const pattern = `^${perm.replace(/\*/g, ".*")}$`;
      return new RegExp(pattern, "i").test(operationId);
    });
  }

  return result;
}

export function useAWSPermissionsCheck(cloudflowId: string) {
  const customerId = useCustomerId();
  const api = useApiContext();

  return useCallback(
    async (account: AWSAccount, permissionsToCheck: string[]): Promise<AwsPermissionsCheckResult> => {
      const {
        data: { awsPermissions, hasPolicy },
      } = await api.request<GrantedAWSPermissions>({
        method: "GET",
        url: `/v1/customers/${customerId}/cloudflow/get-aws-permissions`,
        params: {
          accountId: account.accountId,
          cloudflowId,
        },
      });

      const permissionsNamespace = permissionsToCheck[0].split(":")[0];
      const operationIds = permissionsToCheck.map((permission) => permission.split(":")[1]);
      const permissionsGrantedMap = areAwsPermissionsGranted(awsPermissions, permissionsNamespace, operationIds);
      const missingPermissions = Object.entries(permissionsGrantedMap)
        .filter(([, granted]) => !granted)
        .map(([operationId]) => `${permissionsNamespace}:${operationId}`);

      const permissionsExists = missingPermissions.length === 0;

      let commandToGrantPermission = "";

      if (!permissionsExists) {
        const role = account.roleName;
        const policyName = `DoiT-CloudFlow-${cloudflowId}`;
        commandToGrantPermission = hasPolicy
          ? generateUpdatePolicyCommand(role, policyName, missingPermissions)
          : generateCreatePolicyCommand(role, policyName, missingPermissions);
      }

      return {
        missingPermissions,
        permissionsExists,
        commandToGrantPermission,
      };
    },
    [api, customerId, cloudflowId]
  );
}

export const useCheckAllAccountPermissions = (cloudflowId: string) => {
  const checkPermission = useAWSPermissionsCheck(cloudflowId);

  return useCallback(
    async (accounts: AWSAccount[], permissionsToCheck: string[]) => {
      const checkedPermissions = await Promise.all(
        accounts.map(async (account) => {
          const result = await checkPermission(account, permissionsToCheck);
          return {
            accountName: `${account.accountId}${account.properties?.name ? ` (${account.properties.name})` : ""}`,
            checkResult: result,
          };
        })
      );

      return checkedPermissions;
    },
    [checkPermission]
  );
};
